import {
  Box,
  Drawer,
  IconButton,
  List,
  ListItem,
  Stack,
  Tooltip,
  useTheme,
} from '@mui/material';
import { ArrowLeft, BarChart2, FileText, GitBranch, KeyRound, Link2, ShieldCheck, Users } from 'lucide-react';
import { createContext, useContext, useMemo, useState } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useProjectName } from '@/app/layout/ProjectNameContext';
import Header from '@/app/layout/Header';

interface GitRepo {
  id?: string;
  platform: string;
  url: string;
  branch?: string;
}

interface ProjectSettingsContextType {
  gitRepos: GitRepo[];
  loading: boolean;
  error: string | null;
  refetchGitRepos: () => Promise<void>;
  projectName?: string;
}

const ProjectSettingsContext = createContext<ProjectSettingsContextType | undefined>(undefined);

export const useProjectSettings = () => {
  const context = useContext(ProjectSettingsContext);
  if (!context) {
    throw new Error('useProjectSettings must be used within ProjectSettingsLayout');
  }
  return context;
};

const SIDENAV_WIDTH = 64;

export default function ProjectSettingsLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { appId } = useParams<{ appId: string }>();
  const { projectName, dataSources, configLoading, refreshProject } = useProjectName();

  const isOnboarding = location.pathname.endsWith('/onboarding');

  const [error, setError] = useState<string | null>(null);

  const gitRepos: GitRepo[] = useMemo(
    () => dataSources.filter((ds: any) => ds.platform === 'Git'),
    [dataSources]
  );
  const loading = configLoading;

  const refetchGitRepos = async () => {
    if (!appId) return;
    await refreshProject(appId);
  };

  const menuItems = [
    { icon: ArrowLeft, label: 'Back to Project', path: `/project/${appId}`, isBack: true },
    { icon: FileText, label: 'Project Metadata', path: `/project/${appId}/settings/metadata` },
    { icon: GitBranch, label: 'Repositories', path: `/project/${appId}/settings/repositories` },
    { icon: Link2, label: 'Connects', path: `/project/${appId}/settings/connects` },
    { icon: Users, label: 'Team', path: `/project/${appId}/settings/team` },
    { icon: ShieldCheck, label: 'Management Access', path: `/project/${appId}/settings/management-access` },
    { icon: KeyRound, label: 'API Subscription', path: `/project/${appId}/settings/api-subscription` },
    { icon: BarChart2, label: 'AI Usage', path: `/project/${appId}/settings/usage` },
  ];

  const isActive = (path: string) => {
    if (path.endsWith('/metadata')) {
      return (
        location.pathname === `/project/${appId}/settings` ||
        location.pathname === `/project/${appId}/settings/metadata`
      );
    }
    return location.pathname === path;
  };

  return (
    <Stack sx={{ height: '100%', overflow: 'hidden' }}>
      <Header />
      <Stack
        direction="row"
        sx={{ height: 'calc(100% - 64px)', mt: 0.5, gap: 0.5, overflow: 'hidden', px: 0.5, pb: 0.5 }}
      >
      {!isOnboarding && (
        <Drawer
          variant="permanent"
          sx={{
            width: SIDENAV_WIDTH,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: SIDENAV_WIDTH,
              boxSizing: 'border-box',
              bgcolor:
                theme.palette.mode === 'dark'
                  ? 'rgba(0,0,0,0.2)'
                  : 'grey.50',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1.5,
              position: 'relative',
              height: '100%',
            },
          }}
        >
          <List sx={{ pt: 2 }}>
            {menuItems.map((item) => (
              <ListItem
                key={item.path}
                disablePadding
                sx={{ display: 'block', mb: 1 }}
              >
                <Tooltip title={item.label} placement="right" arrow>
                  <IconButton
                    onClick={() => {
                      if ((item as any).isBack) {
                        // Block navigation back to project page if no git repos configured
                        if (gitRepos.length === 0) {
                          navigate(`/project/${appId}/settings/data-sources`);
                          return;
                        }
                        navigate(item.path, {
                          state: { projectDetails: { appName: projectName } },
                        });
                      } else {
                        navigate(item.path);
                      }
                    }}
                    sx={{
                      width: 48,
                      height: 48,
                      mx: 'auto',
                      display: 'flex',
                      borderRadius: 1.5,
                      bgcolor: isActive(item.path)
                        ? theme.palette.primary.main
                        : 'transparent',
                      color: isActive(item.path) ? 'white' : 'text.secondary',
                      '&:hover': {
                        bgcolor: isActive(item.path)
                          ? theme.palette.primary.dark
                          : 'action.hover',
                      },
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <item.icon size={22} />
                  </IconButton>
                </Tooltip>
              </ListItem>
            ))}
          </List>
        </Drawer>
      )}

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          height: '100%',
          overflow: 'auto',
        }}
      >
        <ProjectSettingsContext.Provider value={{
          gitRepos, loading, error, refetchGitRepos, projectName,
        }}>
          <Outlet />
        </ProjectSettingsContext.Provider>
      </Box>
      </Stack>
    </Stack>
  );
}
